/*
 * Copyright 2023 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { Entity } from '@backstage/catalog-model';

import { InputError } from '@backstage/errors';

import type { EntityMatcherFn } from './matchers/types';

import { createHasMatcher } from './matchers/createHasMatcher';
import { createIsMatcher } from './matchers/createIsMatcher';
import { createKindMatcher } from './matchers/createKindMatcher';
import { createTypeMatcher } from './matchers/createTypeMatcher';

/**
 * @deprecated TODO can be removed after https://github.com/backstage/backstage/pull/31128 is released
 */
const rootMatcherFactories: Record<
  string,
  (
    parameters: string[],
    onParseError: (error: Error) => void,
    negation?: boolean,
  ) => EntityMatcherFn
> = {
  has: createHasMatcher,
  is: createIsMatcher,
  kind: createKindMatcher,
  type: createTypeMatcher,
};

/**
 * Parses a filter expression that decides whether to render an entity component
 * or not. Returns a function that matches entities based on that expression.
 *
 * @remarks
 *
 * Filter strings are on the form `kind:user,group is:orphan`. There's
 * effectively an AND between the space separated parts, and an OR between comma
 * separated parameters. So the example filter string semantically means
 * "entities that are of either User or Group kind, and also are orphans".
 *
 * The `expressionParseErrors` array contains any errors that were encountered
 * during initial parsing of the expression. Note that the parts of the input
 * expression that had errors are ignored entirely and parsing continues as if
 * they didn't exist.
 * @deprecated TODO can be removed after https://github.com/backstage/backstage/pull/31128 is released
 */
export const parseFilterExpression = (
  expression: string,
): {
  expressionParseErrors: Error[];
  filterFn: (entity: Entity) => boolean;
} => {
  const expressionParseErrors: Error[] = [];

  const parts = splitFilterExpression(expression, e =>
    expressionParseErrors.push(e),
  );
  const matchers = parts.flatMap(part => {
    const factory = rootMatcherFactories[part.key];
    if (!factory) {
      const known = Object.keys(rootMatcherFactories).map(m => `'${m}'`);
      expressionParseErrors.push(
        new InputError(
          `'${part.key}' is not a valid filter expression key, expected one of ${known.join(', ')}`,
        ),
      );
      return [];
    }

    const negation = part.negation;

    const matcher = factory(part.parameters, e =>
      expressionParseErrors.push(e),
    );

    return [negation ? (entity: Entity) => !matcher(entity) : matcher];
  });

  const filterFn = (entity: Entity) =>
    matchers.every(matcher => {
      try {
        return matcher(entity);
      } catch {
        return false;
      }
    });

  return {
    expressionParseErrors,
    filterFn,
  };
};

/**
 * @deprecated TODO can be removed after https://github.com/backstage/backstage/pull/31128 is released
 */
export const splitFilterExpression = (
  expression: string,
  onParseError: (error: Error) => void,
): { key: string; negation: boolean; parameters: string[] }[] => {
  const words = expression
    .split(' ')
    .map(w => w.trim())
    .filter(Boolean);

  const result = new Array<{
    key: string;
    negation: boolean;
    parameters: string[];
  }>();

  for (const word of words) {
    const match = /^(not:)?([^:]+):(.+)$/.exec(word);
    if (!match) {
      onParseError(
        new InputError(
          `'${word}' is not a valid filter expression, expected 'key:parameter' form`,
        ),
      );
      continue;
    }
    const key = match[2];
    const parameterText = match[3];
    if (!key || !parameterText) {
      onParseError(
        new InputError(`'${word}' is not a valid filter expression`),
      );
      continue;
    }
    const parameters = parameterText.split(',').filter(Boolean); // silently ignore double commas
    const negation = Boolean(match[1]);
    result.push({ key, negation, parameters });
  }

  return result;
};
